import i18n from "../../i18n";
import { useState, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Progress,
  Text,
  HStack,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import axios from "../../axiosConfig";

const inferKindFromFile = (file) => {
  if (!file) return "file";

  const type = file.type;
  const name = file.name.toLowerCase();

  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";

  if (type === "application/pdf" || name.endsWith(".pdf")) return "document";

  if (name.endsWith(".ppt") || name.endsWith(".pptx")) return "presentation";

  return "file";
};

// Helper: derive a nicer default title from the filename
const deriveTitleFromFilename = (name = "") => {
  if (!name) return "";
  const withoutExt = name.replace(/\.[^/.]+$/, ""); // strip extension
  // Replace dashes/underscores with spaces and capitalise first letter
  const pretty = withoutExt.replace(/[-_]+/g, " ").trim();
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

const WidgetAttachmentUploader = ({ widgetId, onUploaded }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("file");
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    setFile(selected || null);

    if (selected) {
      const inferred = inferKindFromFile(selected);
      setKind(inferred);

      // Only auto-fill title if user hasn't typed something yet
      setTitle((current) => current || deriveTitleFromFilename(selected.name));
    } else {
      setKind("file");
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);

      // 1️⃣ Ask Brain for a presigned URL
      const presignRes = await axios.post("/uploads/presign", {
        filename: file.name,
        content_type: file.type,
      });

      const { upload_url, public_url } = presignRes.data;

      // 2️⃣ Upload directly to S3
      const uploadResponse = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResponse.ok) {
        // Do not log the signed URL or S3 response body.
        console.error("Resource PUT failed", { status: uploadResponse.status });
        throw new Error("Resource PUT failed");
      }

      // 3️⃣ Persist in Brain (now with title + file_name)
      await axios.post(`/widgets/${widgetId}/widget_attachments`, {
        widget_attachment: {
          file_url: public_url,
          file_name: file.name,
          content_type: file.type,
          title: title || deriveTitleFromFilename(file.name), // fallback
          kind, // still sent, even if Brain doesn't use it heavily yet
        },
      });

      toast({ title: "Resource uploaded", status: "success" });

      // Reset UI
      setFile(null);
      setTitle("");
      setKind("file");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setUploading(false);
      onUploaded?.();
    } catch (error) {
      console.error("Resource upload failed", { status: error?.response?.status });
      toast({ title: "Upload failed", description: i18n.t("uploads.resource_failure"), status: "error" });
      setUploading(false);
    }
  };

  const hasFile = !!file;

  return (
    <Box borderWidth="1px" p={4} borderRadius="md" bg="gray.50">
      <VStack spacing={4} align="stretch">
        {/* Resource title / description */}
        <FormControl>
          <FormLabel>Resource title (optional)</FormLabel>
          <Input
            placeholder="e.g. Organisation view slide deck"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            This is the label shown to users. If left blank, we&apos;ll use the file
            name.
          </Text>
        </FormControl>

        {/* File picker */}
        <FormControl>
          <FormLabel>File</FormLabel>
          <HStack spacing={3}>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              colorScheme="orange"
              size="sm"
            >
              Choose file
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              aria-label="Resource file"
              onChange={handleFileChange}
              display="none"
            />
            <Text fontSize="sm" color={hasFile ? "gray.800" : "gray.400"}>
              {hasFile ? file.name : "No file selected"}
            </Text>
          </HStack>

          {hasFile && (
            <HStack spacing={2} mt={2}>
              <Tag size="sm" colorScheme="orange" variant="subtle">
                <TagLabel>Detected type: {kind}</TagLabel>
              </Tag>
              <Text fontSize="xs" color="gray.500">
                ({file.type || "unknown MIME type"})
              </Text>
            </HStack>
          )}
        </FormControl>

        {/* Upload progress indicator */}
        {uploading && (
          <Progress size="xs" isIndeterminate borderRadius="full" />
        )}

        {/* Upload button */}
        <Button
          colorScheme="orange"
          onClick={handleUpload}
          isDisabled={!file || uploading}
          variant="solid"
          alignSelf="flex-start"
        >
          {uploading ? "Uploading…" : "Upload resource"}
        </Button>
      </VStack>
    </Box>
  );
};

export default WidgetAttachmentUploader;